import {
  Fragment,
  Suspense,
  lazy,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Menu, ShieldCheck, Sparkles, TriangleAlert } from 'lucide-react';
import { Avatar, Badge, Banner, Button, ElapsedTimer, Input, Skeleton, cn } from '@hrms/ui';
import { canAnywhere } from '@hrms/shared';
import { ME_QUERY_KEY, tokens, tsr } from '../lib/api.js';
import { useLinkIntentPreload } from '../lib/lazyPage.js';
import { useCopilotStore } from '../features/copilot/store.js';
import { useAuthenticatedSession } from '../session/SessionProvider.js';
import { PageErrorBoundary } from '../components/PageErrorBoundary.js';
import { resolveBreadcrumb } from './breadcrumb.js';
import { MobileNav } from './MobileNav.js';
import { RequireModuleOutlet } from './RequireModuleOutlet.js';
import { isModuleOn } from './moduleRoutes.js';
import { ADMIN_NAV, NAV, visibleNav, type NavItem } from './nav.js';
import { useNavSwitches } from './useNavSwitches.js';
import { ICONS } from './navIcons.js';
import { getSidebarNavItemClasses } from './sidebarNavStyles.js';
import { ThemeToggle } from '../components/ThemeToggle.js';
import { NotificationBell } from '../features/notifications/NotificationBell.js';
import { TaskReminderPopup } from '../features/projects/TaskReminderPopup.js';
import { PushPermissionBanner } from '../features/notifications/PushPermissionBanner.js';
import { ChatNavButton } from '../features/chat/ChatNavButton.js';
import { SocketProvider } from '../lib/socket.js';
import { GlobalSearch } from '../features/search/GlobalSearch.js';
import { platformPortalOrigin } from '../lib/platformHost.js';
import { BillingOverdueBanner } from '../features/billing/BillingOverdueBanner.js';
import { AssetNavItem } from './assets/components/AssetSubmenuFlyout.js';
import { OrgSwitcher } from '../features/auth/OrgSwitcher.js';
import { ProfileMenu } from '../features/auth/ProfileMenu.js';
import { TrialBanner } from '../features/agreements/TrialBanner.js';
import { LinkAccountBanner } from '../features/company-group/LinkAccountBanner.js';
import { trainingKeys } from './training/hooks.js';
import { isOverrunCoursePage } from '../features/learning/redirects.js';
import { BrandMark } from '../components/BrandMark.js';
import { SetupNavItem } from '../features/setup/SetupNavItem.js';
import { BackToSetupBar } from '../features/setup/BackToSetupBar.js';
import { AddFirstStaffBar } from '../features/setup/activation/AddFirstStaffBar.js';
import { AddTeamModalProvider } from '../features/setup/activation/AddTeamModalProvider.js';
import { PRODUCT_NAME } from '../lib/brand.js';
import { PASSWORD_MIN_LENGTH } from './auth/authFields.js';
import { timeOfDayGreeting } from '../lib/datetime.js';

// Loaded only when used: the copilot dock (and the markdown renderer under it) for people with the
// copilot on, the regularisation form when a training overrun asks for one. Neither is part of the
// first load every other person waits on.
const CopilotDock = lazy(() =>
  import('../features/copilot/CopilotDock.js').then((m) => ({ default: m.CopilotDock })),
);
const RegularisationForm = lazy(() =>
  import('../features/training/RegularisationForm.js').then((m) => ({
    default: m.RegularisationForm,
  })),
);

/**
 * The web app shell: persistent sidebar + topbar around a routed content area.
 *
 * WHY WEB GETS A DIFFERENT LAYOUT FROM MOBILE, deliberately. Mobile stacks one column under a
 * gradient hero because a phone has one column and a thumb. A desktop browser has 1400px of
 * horizontal room and a mouse, so the same content laid out as a single stretched column wastes
 * most of the viewport and buries the approvals queue below the fold. The two surfaces share
 * TOKENS and components, not layout — see packages/ui/src/styles/tokens.css.
 *
 * The sidebar is hidden below `lg` (a 224px rail costs more than it returns at tablet width) and
 * replaced by `MobileNav`, a left-edge drawer triggered from the topbar — see MobileNav.tsx for
 * why that's a full drawer rather than a bottom bar (`NAV` + `ADMIN_NAV` together routinely exceed
 * a bottom bar's ~5 usable slots).
 *
 * The copilot dock is the third flex sibling, after the main column — a right rail that narrows
 * the page rather than covering it (see CopilotDock.tsx). The main column's `min-w-0` is what
 * lets it give up width to the rail instead of overflowing; keep it.
 *
 * Rendered only inside RequireAuth, so the session is always authenticated here.
 */

export function AppShell() {
  const { me, actor } = useAuthenticatedSession();
  const [navOpen, setNavOpen] = useState(false);
  const navTriggerRef = useRef<HTMLButtonElement>(null);
  // A courtesy gate, same as visibleNav: the API refuses the routes to anyone without it.
  const copilotEnabled =
    canAnywhere(actor, 'ai.copilot:use') && isModuleOn(me.enabledModules, 'ai_copilot');
  useCopilotShortcut(copilotEnabled);
  // Pages are separate chunks (routes/pages.ts); start fetching one as soon as a link to it is
  // pointed at, focused or touched, so the click rarely waits.
  useLinkIntentPreload();
  // Keys the page error boundary below, so leaving a page that failed clears the error.
  const { pathname } = useLocation();

  // Enforced on the API too (`authenticate` answers 403 PASSWORD_CHANGE_REQUIRED for everything but
  // the change flow), so this is the UX half: render ONLY the blocking step — no sidebar, no routed
  // content, no SocketProvider (nothing else may run under a temporary password). The flag clears
  // when `ChangePasswordDialog` invalidates the `me` query, which re-renders this into the app.
  if (me.person.mustChangePassword) {
    return <RequiredPasswordChange />;
  }

  return (
    // SocketProvider opens the realtime connection with the signed-in user's access token and is
    // what `NotificationBell` (via useNotificationSocket) reads — it must sit above the bell, and
    // only here: AppShell renders solely inside RequireAuth, so a token always exists. Nothing
    // mounted it before, and every signed-in page threw "called outside <SocketProvider>".
    <SocketProvider>
      {/* The one Add your team modal, opened from Home's activation card, the bar below and the
       * welcome redirect alike (see AddTeamModalProvider). */}
      <AddTeamModalProvider>
        <div className="min-h-dvh bg-bg-subtle">
          <div className="flex">
            <Sidebar />
            <div className="flex min-w-0 flex-1 flex-col">
              {/* One sticky wrapper around BOTH the topbar and the training overrun banner, rather
               * than each stacking its own `sticky top-0` independently — two siblings that both
               * claim `top: 0` overlap instead of stacking (a real CSS gotcha, not a framework
               * quirk), so `Topbar`'s own `<header>` is deliberately NOT sticky any more; this div
               * is the only sticky element in the column. */}
              <div className="sticky top-0 z-20 flex flex-col">
                <Topbar
                  navOpen={navOpen}
                  onNavOpenChange={setNavOpen}
                  navTriggerRef={navTriggerRef}
                />
                <TrainingOverrunBanner />
              </div>
              {me.person.mustChangePassword && <ChangePasswordBanner />}
              {canAnywhere(actor, 'org.billing:read') && <BillingOverdueBanner />}
              <PushPermissionBanner />
              <TrialBanner />
              <LinkAccountBanner />
              <BackToSetupBar />
              <AddFirstStaffBar />
              <main className="w-full flex-1 px-4 py-6 sm:px-6">
                {/* Every page below is lazy. This boundary keeps the frame on screen while one loads;
                 * a navigation keeps showing the current page until the next is ready (React Router
                 * runs navigations in a transition), so this shows only on a first, direct load. */}
                {/* A page that fails to load or render shows a "Reload" message here, inside the
                 * frame — without a boundary React unmounts the whole app and the screen goes blank. */}
                <PageErrorBoundary key={pathname}>
                  <Suspense fallback={<Skeleton className="h-64" />}>
                    <RequireModuleOutlet />
                  </Suspense>
                </PageErrorBoundary>
              </main>
            </div>
            {copilotEnabled && (
              <Suspense fallback={null}>
                <CopilotDock />
              </Suspense>
            )}
          </div>
          <MobileNav
            open={navOpen}
            onClose={() => setNavOpen(false)}
            returnFocusRef={navTriggerRef}
          />
          {/* Fixed-position overlay, so where it sits in this tree doesn't matter — only that it's
           * inside SocketProvider (same requirement as NotificationBell above). */}
          <TaskReminderPopup />
        </div>
      </AddTeamModalProvider>
    </SocketProvider>
  );
}

/**
 * The global half of the training clock-out gate (Training_Module_Plan.md §6.1's overrun banner,
 * §6.4's "Employee — overrun" flow): a sticky, danger-toned strip with a live count-up timer, shown
 * on every screen once `trainingMe.today.overrun` is non-null — not just Attendance and not just
 * Training. Reads the SAME query key `routes/training/hooks.ts`'s `useTrainingToday` polls (used by
 * `AttendanceTodayStatus.tsx`'s own gate) — one cached `trainingMe.today` result behind every mount
 * point, never a second copy of the polling logic.
 *
 * Hidden on THIS overrun's own course page — `/learning/<id or slug>`, or the old `/training/…`
 * form for the instant before it redirects — where the course player shows its own notice
 * (`isOverrunCoursePage`). Matched on that one course, not a blanket prefix check, so it still
 * shows on My learning, Certificates and every other course, where nothing else says so.
 */
function TrainingOverrunBanner() {
  const { actor } = useAuthenticatedSession();
  const location = useLocation();
  const [regularisationOpen, setRegularisationOpen] = useState(false);
  const trainingReadEnabled = canAnywhere(actor, 'learning:read');

  const todayQuery = tsr.trainingMe.today.useQuery({
    queryKey: trainingKeys.today(),
    refetchInterval: 60_000,
    enabled: trainingReadEnabled,
  });
  const overrun = todayQuery.data?.status === 200 ? todayQuery.data.body.overrun : null;

  // An overrun needs a deadline to run over — a no-deadline assignment can never drive this banner.
  if (!overrun || !overrun.dueAt) return null;
  if (isOverrunCoursePage(location.pathname, overrun)) return null;

  return (
    <>
      <Banner
        tone="danger"
        icon={<TriangleAlert className="size-4" />}
        action={
          <Button variant="secondary" size="sm" onClick={() => setRegularisationOpen(true)}>
            Submit regularisation
          </Button>
        }
      >
        <span className="flex flex-wrap items-center gap-x-2">
          <span className="font-semibold">Training overrun</span>
          <span className="truncate">
            &lsquo;{overrun.title}&rsquo; is past its deadline — clock-out is locked.
          </span>
          <ElapsedTimer
            startedAt={new Date(overrun.dueAt)}
            ariaLabel="Time overrun"
            className="font-semibold"
          />
        </span>
      </Banner>
      {regularisationOpen && (
        <Suspense fallback={null}>
          <RegularisationForm
            assignmentId={overrun.assignmentId}
            onClose={() => setRegularisationOpen(false)}
            onSuccess={() => {
              setRegularisationOpen(false);
              void todayQuery.refetch();
            }}
          />
        </Suspense>
      )}
    </>
  );
}

/**
 * ⌘K / Ctrl+K toggles the dock from anywhere. Ignored while typing in any other input, textarea
 * or editable region — a search box or a form field owns its own shortcuts — except the copilot's
 * own composer, where ⌘K should still put the dock away.
 */
function useCopilotShortcut(enabled: boolean) {
  const toggle = useCopilotStore((s) => s.toggle);
  useEffect(() => {
    if (!enabled) return;
    function onKeyDown(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      if (event.altKey || event.shiftKey) return;
      const target = event.target as HTMLElement | null;
      const typing =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);
      if (typing && !target.hasAttribute('data-copilot-composer')) return;
      event.preventDefault();
      toggle();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, toggle]);
}

function Sidebar() {
  const { actor, me } = useAuthenticatedSession();
  const switches = useNavSwitches();
  const primary = visibleNav(actor, NAV, me.enabledModules, switches);
  // The whole group disappears for anyone with no admin permission at all — an empty "Admin"
  // heading would only advertise that there is something they can't see.
  const admin = visibleNav(actor, ADMIN_NAV, me.enabledModules, switches);

  return (
    <aside className="sticky top-0 z-30 hidden h-dvh w-56 shrink-0 flex-col border-r border-sidebar-border bg-sidebar-bg px-3 py-5 lg:flex">
      {/* Everything above the footer shares this wrapper's own `flex-1` rather than the `<aside>`
       * itself, so the footer below always sits at the bottom of the rail — with or without an
       * Admin section — instead of leaving that space blank when Admin (the only child that used
       * to carry `flex-1`) is absent for this actor. */}
      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div className="flex items-center gap-2.5 px-2">
          <BrandMark size={32} className="ring-1 ring-sidebar-fg/25" />
          <span className="min-w-0 truncate font-display text-sm font-bold leading-tight tracking-tight text-sidebar-fg">
            {PRODUCT_NAME}
          </span>
        </div>

        {/* Pinned above the scrolling menu, so an owner part-way through setup always sees how far
         * they are; renders nothing once the core steps are done or for anyone who cannot do them. */}
        <SetupNavItem />

        {/* The whole rail scrolls together — Core Modules and Administration alike — when it's
         * taller than the viewport, keeping every item legible against the sidebar background
         * instead of spilling onto the page behind it. A plain `<div>`, not a `<nav>`, so the two
         * sections below keep their own separate `<nav aria-label>` landmarks (screen-reader
         * navigation, and what AppShell.test.tsx queries by) while still sharing one scroll box.
         * Items in EITHER section can carry a flyout (Performance, Organisation, Employee,
         * Payroll, ...); the flyout is portalled to <body> with `position: fixed` and JS-computed
         * coordinates (see PerformanceNavItem), so it is not a DOM descendant of this container at
         * all and this `overflow-y: auto` cannot clip it. The two sections used to be split into
         * separate SCROLL regions specifically to dodge that clipping, back when the flyout was
         * still an ordinary absolutely-positioned child — the portal made the split unnecessary,
         * and it only ever bought Core Modules the cost of never scrolling with the rest of the
         * rail. Scrollbar hidden (still fully scrollable via wheel/trackpad/keyboard) since a
         * visible bar reads as chrome on a rail this narrow. */}
        <div
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          // A row clipped clean in half by the container's own edge reads as broken chrome, not
          // as an affordance to scroll — this fades the last visible row out instead, the same
          // "there's more below" signal a scrollable list usually gives some other way (a visible
          // scrollbar, in this case deliberately hidden — see the comment above).
          style={{
            maskImage: 'linear-gradient(to bottom, black calc(100% - 24px), transparent)',
          }}
        >
          <nav className="flex flex-col gap-0.5" aria-label="Main">
            <p className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-sidebar-fg-muted">
              Core Modules
            </p>
            {primary.map((item) => (
              <SidebarLink key={item.to} item={item} />
            ))}
          </nav>

          {admin.length > 0 && (
            <nav className="flex flex-col gap-0.5" aria-label="Admin">
              <p className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-sidebar-fg-muted">
                Administration
              </p>
              {admin.map((item) => (
                <SidebarLink key={item.to} item={item} />
              ))}
            </nav>
          )}
        </div>
      </div>

      <SidebarFooter />
    </aside>
  );
}

function SidebarFooter() {
  const { me } = useAuthenticatedSession();
  // From `/auth/me`, which every page already waits on. This used to search the employee list by
  // email on every page load just for this one line — the slowest request the frame made.
  const designation = me.designation;

  return (
    <Link
      to="/my-details"
      className="mt-3 flex shrink-0 items-center gap-2.5 rounded-lg bg-sidebar-hover-bg px-2.5 py-2.5 text-sidebar-fg transition-colors hover:bg-sidebar-active-bg"
    >
      <Avatar
        name={me.person.displayName}
        imageUrl={me.person.photoUrl}
        colorSeed={me.person.id}
        size="sm"
        className="shrink-0"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold text-sidebar-fg">{me.person.displayName}</p>
        <p className="truncate text-[11px] text-sidebar-fg-muted">
          {designation || me.person.email}
        </p>
      </div>
      <ChevronRight className="size-3.5 shrink-0 text-sidebar-fg-muted" aria-hidden="true" />
    </Link>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  if (
    item.label === 'Asset' ||
    item.label === 'Assets' ||
    item.to.startsWith('/asset-management')
  ) {
    return <AssetNavItem item={item} />;
  }

  const hasFlyout = Boolean(
    (item.children && item.children.length > 0) || (item.subItems && item.subItems.length > 0),
  );
  if (hasFlyout) {
    return <PerformanceNavItem item={item} />;
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) => getSidebarNavItemClasses(isActive)}
    >
      {ICONS[item.to]}
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

/**
 * A nav item that fans out to several sections — Performance's 5 sections via `children`, or
 * Attendance's submodules via `subItems` (generic despite the name; dispatched for ANY item with
 * a flyout, not just Performance) — as a hover/focus flyout rather than a flat link. Hover-only
 * would exclude keyboard and touch/tablet users entirely, so the flyout also opens on focus-within
 * (`group-focus-within:block`), and the parent label itself still navigates to `item.to` on
 * click/Enter.
 *
 * `z-30` on the `<aside>` ancestor is load-bearing: `position: sticky` creates an implicit stacking
 * context, and without an explicit z-index the main content area (a later DOM sibling) paints over
 * the whole sidebar — including this flyout's absolutely-positioned overflow — regardless of any
 * z-index set here.
 */
function PerformanceNavItem({ item }: { item: NavItem }) {
  const childLinks: { to: string; label: string; status?: 'live' | 'soon'; section?: string }[] =
    item.children
      ? item.children.map((child) => ({
          to: child.to,
          label: child.label,
          status: child.status,
          section: child.section,
        }))
      : (item.subItems ?? []).map((sub) => ({
          to: `${item.to}/${sub.path}`,
          label: sub.label,
          section: sub.section,
        }));

  const location = useLocation();
  const triggerRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const open = pos !== null;

  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);
  const clearTimers = () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
  };

  const openFlyout = () => {
    clearTimers();
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.top, left: rect.right });
  };
  const closeFlyout = () => {
    clearTimers();
    setPos(null);
  };

  // Hover intent, for the POINTER only (keyboard focus opens and closes immediately). The flyout can
  // sit higher than its row — it is clamped to stay on screen — so the pointer crosses neighbouring
  // rows on its way to it; closing the instant it leaves this row shut the menu before it could be
  // reached. A short grace period before closing fixes that, and a short delay before opening keeps
  // sweeping past a row from popping its menu over the one being reached for.
  const scheduleOpen = () => {
    window.clearTimeout(closeTimer.current);
    if (open) return;
    window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(openFlyout, 80);
  };
  const scheduleClose = () => {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(closeFlyout, 250);
  };

  // Picking a page (or any navigation) closes it; timers must not outlive the item.
  useEffect(closeFlyout, [location.pathname]);
  useEffect(() => clearTimers, []);

  // Keep a tall flyout inside the viewport: an item near the bottom of the rail (Payroll, with nine
  // pages) would otherwise open downward off-screen. Measured after it renders, so it only ever
  // moves UP, and the condition stops it looping.
  useLayoutEffect(() => {
    if (!pos || !flyoutRef.current) return;
    const maxTop = window.innerHeight - flyoutRef.current.offsetHeight - 8;
    if (pos.top > maxTop) setPos({ top: Math.max(8, maxTop), left: pos.left });
  }, [pos]);

  // A fixed flyout does not follow the rail if it scrolls underneath, so close instead of drifting.
  useEffect(() => {
    if (!open) return;
    const onScroll = (event: Event) => {
      if (event.target instanceof Node && flyoutRef.current?.contains(event.target)) return;
      closeFlyout();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  const insideThis = (node: EventTarget | null) =>
    node instanceof Node &&
    (triggerRef.current?.contains(node) || flyoutRef.current?.contains(node) || false);

  return (
    // `pr-2` (not the flyout's own margin) makes the visual gap to the flyout part of THIS box's
    // own hoverable area — the flyout opens at this box's right edge, so the pointer never crosses
    // a dead zone between the link and the menu. The flyout is a React child of this element (via
    // the portal below), so React's enter/leave treat moving onto it as still being inside.
    <div
      ref={triggerRef}
      className="relative w-full"
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocus={openFlyout}
      onBlur={(event) => {
        if (!insideThis(event.relatedTarget)) closeFlyout();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') closeFlyout();
      }}
    >
      <NavLink
        to={item.to}
        end={Boolean(item.subItems)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={({ isActive }) => getSidebarNavItemClasses(isActive)}
      >
        {ICONS[item.to]}
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        <ChevronRight className="size-3 shrink-0 opacity-80" aria-hidden="true" />
      </NavLink>

      {/* Portalled to <body> with fixed coordinates, NOT an absolutely positioned child. The Admin
       * group of the rail is a scroll container with a fade mask, and either of those clips
       * anything that overflows it — a real CSS rule (`overflow-y: auto` forces `overflow-x` to
       * compute as `auto` too) — so an in-place flyout was invisible for every Admin item with
       * pages, Payroll included. Rendered outside that box, it is clipped by nothing.
       * `max-h-[80vh]` + scroll covers a short screen. */}
      {open &&
        createPortal(
          <div
            ref={flyoutRef}
            style={{ top: pos.top, left: pos.left }}
            className="fixed z-40 flex max-h-[80vh] w-60 flex-col gap-0.5 overflow-y-auto rounded-lg border border-sidebar-border bg-sidebar-submenu-bg p-1.5 shadow-card"
            role="menu"
            aria-label={item.label}
          >
            <div
              aria-hidden="true"
              className="mb-1 flex items-center justify-between border-b border-sidebar-border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-sidebar-fg-muted"
            >
              <span>{item.submenuLabel ?? item.label}</span>
              <span className="text-[9px] font-normal normal-case">Subsections</span>
            </div>
            {childLinks.map((child, index) => {
              // A heading above the first visible item of each named section. Derived from the
              // links that survived the permission filter, so a role that sees none of a
              // section's items never sees its heading either.
              const startsSection =
                Boolean(child.section) && child.section !== childLinks[index - 1]?.section;
              // Without `end`, react-router's NavLink matches by PREFIX: '/admin/onboarding'
              // stays "active" on '/admin/onboarding/templates' too, because that path literally
              // starts with it — a false positive between two unrelated sibling links, not a real
              // parent/child page relationship. Forcing exact matching whenever some OTHER sibling
              // in this same flyout extends this one's path (adds a '/segment' after it) fixes
              // that without changing matching for every other flyout, most of which have no such
              // collision and are left exactly as before.
              const isPrefixOfSibling = childLinks.some(
                (sibling) => sibling.to !== child.to && sibling.to.startsWith(`${child.to}/`),
              );
              return (
                <Fragment key={child.to}>
                  {startsSection && (
                    <div
                      role="presentation"
                      className="mb-0.5 mt-1.5 border-t border-sidebar-border px-2.5 pb-0.5 pt-2 text-[10px] font-bold uppercase tracking-wider text-sidebar-fg-muted"
                    >
                      {child.section}
                    </div>
                  )}
                  {child.status === 'soon' ? (
                    <span className="flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-xs text-sidebar-fg-muted">
                      {child.label}
                      <Badge tone="neutral">Coming soon</Badge>
                    </span>
                  ) : (
                    <NavLink
                      to={child.to}
                      end={child.to === item.to || isPrefixOfSibling}
                      role="menuitem"
                      className={({ isActive }) =>
                        cn(
                          'flex items-center rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                          item.showActiveDot && isActive && 'justify-between',
                          isActive
                            ? 'bg-sidebar-active-bg font-semibold text-sidebar-active-fg'
                            : 'text-sidebar-fg-muted hover:bg-sidebar-hover-bg hover:text-sidebar-fg',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {child.label}
                          {item.showActiveDot && isActive && (
                            <span
                              className="size-1.5 shrink-0 rounded-full bg-success"
                              aria-hidden="true"
                            />
                          )}
                        </>
                      )}
                    </NavLink>
                  )}
                </Fragment>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}

interface TopbarProps {
  navOpen: boolean;
  onNavOpenChange: (open: boolean) => void;
  navTriggerRef: RefObject<HTMLButtonElement | null>;
}

function Topbar({ navOpen, onNavOpenChange, navTriggerRef }: TopbarProps) {
  const { me, actor } = useAuthenticatedSession();
  const copilotOpen = useCopilotStore((s) => s.open);
  const toggleCopilot = useCopilotStore((s) => s.toggle);
  const copilotEnabled =
    canAnywhere(actor, 'ai.copilot:use') && isModuleOn(me.enabledModules, 'ai_copilot');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const logout = tsr.auth.logout.useMutation();

  // Dates render in the viewer's locale; the employee's own location timezone governs anything
  // that decides a WORKING day (see CLAUDE.md invariant 3) — this greeting is not that.
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  });
  const greetingName = me.person.displayName.split(/\s+/)[0] ?? me.person.displayName;
  const breadcrumb = resolveBreadcrumb(location.pathname);

  const signOut = () => {
    logout.mutate(
      {},
      {
        onSettled: async () => {
          await tokens.clear();
          queryClient.removeQueries();
          navigate('/login', { replace: true });
        },
      },
    );
  };

  return (
    <header className="sticky top-0 z-10 border-b border-border bg-surface/95 pt-[env(safe-area-inset-top)] backdrop-blur">
      {/* gap-2 on a phone: at 320px the icon cluster plus a gap-4 spacing pushed Sign out past the
       * edge. The roomier gap returns from `sm` up, where there is room for it. */}
      <div className="flex w-full items-center gap-2 px-3 py-3 sm:gap-4 sm:px-6">
        <button
          ref={navTriggerRef}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={navOpen}
          aria-controls="mobile-nav"
          aria-label="Open navigation"
          onClick={() => onNavOpenChange(true)}
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-bg-subtle text-fg-muted transition-colors hover:text-fg sm:size-9 lg:hidden"
        >
          <Menu className="size-4" />
        </button>

        {/* Hidden on a phone: the greeting already names the user, and the ~48px is what lets it
         * read "Hi, Rahul" instead of being cut to "Goo…". */}
        <span className="hidden shrink-0 sm:inline-flex">
          <Avatar
            name={me.person.displayName}
            imageUrl={me.person.photoUrl}
            colorSeed={me.person.id}
            size="md"
            className="shrink-0"
          />
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-fg-subtle">
            {breadcrumb.section ? (
              <>
                {breadcrumb.root}
                {' › '}
                {breadcrumb.section}
                {breadcrumb.page ? ` › ${breadcrumb.page}` : ''}
                {breadcrumb.subpage ? ` › ${breadcrumb.subpage}` : ''}
              </>
            ) : (
              today
            )}
          </p>
          <h1 className="truncate font-display text-base font-bold text-fg sm:text-lg">
            <span className="sm:hidden">Hi, {greetingName}</span>
            <span className="hidden sm:inline">
              Good {timeOfDayGreeting()}, {greetingName}
            </span>
          </h1>
        </div>

        {/* Sits right beside the breadcrumb/greeting block (rather than off past the bell) and
         * shares flex-1 with it, capped at max-w-md — grows with the topbar instead of hugging its
         * own content, without crowding out the identity block or the icon cluster on the right.
         * For an actor with `ai.copilot:use` the pill opens the copilot dock (and ⌘K does the same
         * from anywhere — see useCopilotShortcut). Everyone else gets GlobalSearch (employee search
         * plus page jump) in the same slot, which binds ⌘K itself — the two are mutually exclusive
         * on purpose, so ⌘K is never bound twice. Hidden below `lg` alongside the sidebar/hamburger
         * toggle (this app's breakpoints are sm/lg/xl, not `md`) so it only appears once the full
         * desktop chrome is present. */}
        {copilotEnabled ? (
          <button
            type="button"
            onClick={toggleCopilot}
            aria-expanded={copilotOpen}
            aria-label="Toggle copilot"
            className="hidden flex-1 max-w-md items-center gap-2 rounded-lg border border-border bg-bg-subtle px-3.5 py-2.5 text-sm text-fg-subtle transition-colors hover:border-border-strong hover:text-fg-muted lg:flex"
          >
            <Sparkles className="size-4 shrink-0 text-accent" />
            <span className="flex-1 truncate text-left">Ask the copilot or jump to…</span>
            <kbd className="shrink-0 rounded border border-border bg-surface px-1.5 py-px text-[10px] text-fg-muted">
              ⌘K
            </kbd>
          </button>
        ) : (
          <GlobalSearch />
        )}

        {me.session.impersonatedBy && <Badge tone="warning">Impersonating</Badge>}

        {/* The nav-bar "company switcher" the platform owner asked for. It renders only when this
            session was minted by an "enter organisation" grant (see platformContract) — an
            ordinary employee's session never carries impersonatedByPlatformAdmin, so this is
            invisible to everyone else. "Switch" re-opens the org picker rather than duplicating
            its search UI in a dropdown — the platform token that authorised entering THIS org is
            still held separately (see lib/platformApi.ts) and authorises entering the next one. */}
        {me.session.impersonatedByPlatformAdmin && (
          <button
            type="button"
            onClick={() => navigate('/platform/organizations')}
            className="hidden items-center gap-1.5 rounded-full border border-warning/30 bg-warning-subtle px-3 py-1.5 text-xs font-medium text-warning transition-colors hover:bg-warning-subtle/70 sm:flex"
          >
            <ShieldCheck className="size-3.5" />
            Platform owner — switch company
          </button>
        )}

        {/* Company-management portal (billing / who has paid). Same visibility rule as the button
            above; the pages behind it are additionally guarded by RequirePlatformOwner and, more
            importantly, every API route by authenticatePlatformAdmin. */}
        {me.session.impersonatedByPlatformAdmin && (
          <button
            type="button"
            onClick={() => window.location.assign(platformPortalOrigin())}
            className="hidden items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-medium text-fg transition-colors hover:bg-bg-subtle sm:flex"
          >
            <ShieldCheck className="size-3.5" />
            Company management
          </button>
        )}

        <OrgSwitcher />

        <ThemeToggle />

        {isModuleOn(me.enabledModules, 'chat') && <ChatNavButton />}

        <NotificationBell />

        <ProfileMenu onSignOut={signOut} signingOut={logout.isPending} />
      </div>
    </header>
  );
}

// The dismissable nudge for a temporary (not-yet-expired) password, distinct from
// RequiredPasswordChange's full-screen blocker below: this one can be closed and revisited later,
// so its own dialog (ChangePasswordNudgeDialog) takes an `onClose` rather than the `required` mode
// ChangePasswordDialog implements for the blocking flow — two different components on purpose, not
// a naming collision to merge away.
function ChangePasswordBanner() {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-2 border-b border-warning/30 bg-warning-subtle px-4 py-2 text-sm text-warning sm:px-6"
    >
      <span>You are using a temporary password. Change it before it expires.</span>
      <button
        type="button"
        className="font-medium underline hover:no-underline"
        onClick={() => setDialogOpen(true)}
      >
        Change password now
      </button>
      {dialogOpen && <ChangePasswordNudgeDialog onClose={() => setDialogOpen(false)} />}
    </div>
  );
}

/** Same fixed-overlay/centered-card pattern as NotificationDetailPopup above. */
function ChangePasswordNudgeDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState<string | null>(null);
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);

  const changePassword = tsr.auth.changePassword.useMutation();

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setCurrentPasswordError(null);
    setNewPasswordError(null);

    // Mirrors @hrms/shared's newPasswordSchema (length-only, NIST SP 800-63B — see
    // ResetPassword.tsx's identical check) so a round trip isn't needed to learn this.
    if (newPassword.length < PASSWORD_MIN_LENGTH || newPassword.length > 128) {
      setNewPasswordError(`New password must be ${PASSWORD_MIN_LENGTH}–128 characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setNewPasswordError('Passwords do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setNewPasswordError('The new password must differ from the current one.');
      return;
    }

    changePassword.mutate(
      { body: { currentPassword, newPassword } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
          onClose();
        },
        onError: (mutationError) => {
          const status = (mutationError as { status?: number } | undefined)?.status;
          const body = (mutationError as { body?: unknown } | undefined)?.body;
          const message =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof body.message === 'string'
              ? body.message
              : "Can't reach the server. Check your connection and try again.";
          if (status === 401) {
            setCurrentPasswordError(message);
          } else {
            setNewPasswordError(message);
          }
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/30 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-surface p-5 shadow-lg">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="font-display text-base font-bold text-fg">Change password</h2>
          <button
            type="button"
            className="text-fg-muted hover:text-fg"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <Input
            label="Current password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            error={currentPasswordError}
            required
          />
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            hint={`${PASSWORD_MIN_LENGTH}–128 characters.`}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            error={newPasswordError}
            required
          />
          <Input
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
          />

          <div className="mt-1 flex justify-end gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" loading={changePassword.isPending}>
              Change password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Full-screen blocker: the dialog in `required` mode over an otherwise empty page, plus sign-out. */
function RequiredPasswordChange() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logout = tsr.auth.logout.useMutation();
  const signOut = () => {
    logout.mutate(
      {},
      {
        onSettled: async () => {
          await tokens.clear();
          queryClient.removeQueries();
          navigate('/login', { replace: true });
        },
      },
    );
  };
  return (
    <div className="min-h-dvh bg-bg-subtle">
      <ChangePasswordDialog required onSignOut={signOut} signingOut={logout.isPending} />
    </div>
  );
}

/** Same fixed-overlay/centered-card pattern as NotificationDetailPopup above. */
function ChangePasswordDialog({
  onClose,
  required = false,
  onSignOut,
  signingOut = false,
}: {
  onClose?: () => void;
  /** Blocking mode: no close button, no Cancel, no Escape/backdrop dismissal; offers sign-out instead. */
  required?: boolean;
  onSignOut?: () => void;
  signingOut?: boolean;
}) {
  const queryClient = useQueryClient();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState<string | null>(null);
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);

  const changePassword = tsr.auth.changePassword.useMutation();

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setCurrentPasswordError(null);
    setNewPasswordError(null);

    // Mirrors @hrms/shared's newPasswordSchema (length-only, NIST SP 800-63B — see
    // ResetPassword.tsx's identical check) so a round trip isn't needed to learn this.
    if (newPassword.length < PASSWORD_MIN_LENGTH || newPassword.length > 128) {
      setNewPasswordError(`New password must be ${PASSWORD_MIN_LENGTH}–128 characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setNewPasswordError('Passwords do not match.');
      return;
    }
    if (!required && newPassword === currentPassword) {
      setNewPasswordError('The new password must differ from the current one.');
      return;
    }

    changePassword.mutate(
      // The forced change after a temporary password asks only for the new one: they just signed in
      // with the old one, so the API takes the request without it (auth.contract.ts).
      { body: required ? { newPassword } : { currentPassword, newPassword } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
          onClose?.();
        },
        onError: (mutationError) => {
          const status = (mutationError as { status?: number } | undefined)?.status;
          const body = (mutationError as { body?: unknown } | undefined)?.body;
          const message =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof body.message === 'string'
              ? body.message
              : "Can't reach the server. Check your connection and try again.";
          if (status === 401) {
            setCurrentPasswordError(message);
          } else {
            setNewPasswordError(message);
          }
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/30 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-surface p-5 shadow-lg">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="font-display text-base font-bold text-fg">Change password</h2>
          {!required && (
            <button
              type="button"
              className="text-fg-muted hover:text-fg"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>
        {required && (
          <p className="mb-4 text-sm text-fg-muted">
            You are using a temporary password. Choose a new one to continue.
          </p>
        )}

        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          {!required && (
            <Input
              label="Current password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              error={currentPasswordError}
              required
            />
          )}
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            hint={`${PASSWORD_MIN_LENGTH}–128 characters.`}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            error={newPasswordError}
            required
          />
          <Input
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
          />

          <div className="mt-1 flex justify-end gap-2">
            {required ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onSignOut}
                loading={signingOut}
              >
                Sign out
              </Button>
            ) : (
              <Button type="button" variant="secondary" size="sm" onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button type="submit" variant="primary" size="sm" loading={changePassword.isPending}>
              Change password
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
